import React, { useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { Select } from 'antd';

import { RootState } from '../../../../redux/reducers';
import {
  studentFinderActionsInterfaces,
  studentFinderActions,
  studentFinderActionsSelectors,
} from '../../../../redux/staticViews/studentFinder';

import { StudentSearchContainer } from './StudentSearch.style';

const { getStudentList, getStudentListCancel, chooseSingleStudent, changeSelectOpen } = studentFinderActions;
const { Option } = Select;

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;

type StudentFinderProps = StaticViewComponent & MapStateToProps & MapDispatchToProps & RouteComponentProps;

/**
 *  Komponent wyszukiwarki w zakładce Studenci. Po wpisaniu frazy (imię, nazwisko lub numer indeksu) zostanie wysłane zapytnie do API o listę studentów (muszą zostać wpisane co najmniej 3 znaki).
 *  Aby zobaczyć szczegóły studneta oraz przedmioty należy wybrać pozycję z listy. Szczegóły pojawią się natychmiastowo, natomiast lista przedmiotów musi zostać pobrana.
 *
 */
// export dodany w celu wyświetlenia komentarza w dokumentacji, ponieważ opakowane funkcje mają problem z wyświetlaniem
export const StudentFinder = ({
  loading,
  studentList,
  singleStudent,
  getStudentList,
  chooseSingleStudent,
  selectOpen,
  changeSelectOpen,
}: StudentFinderProps): JSX.Element => {
  const { t } = useTranslation();
  const selectAntd = useRef<any>();

  useEffect(() => {
    if (selectAntd) {
      selectAntd.current.focus();
    }
  }, [studentList]);

  const getOptionsData = debounce((value: string) => {
    getStudentList(value);
  }, 1000);

  const handleSearch = (value: string): void => {
    const trimmed = value.trim();
    if (trimmed !== '' && trimmed.length > 2) getOptionsData(value);
  };

  const handleChange = (value: number): void => {
    chooseSingleStudent(value);
  };

  const options = studentList.map(student => (
    <Option key={`${student.studentId}_${student.lp}`} className='custom_option' value={student.studentId}>
      {student.studentImie} {student.studentNazwisko} {student.studentAlbum}
    </Option>
  ));

  return (
    <StudentSearchContainer>
      <h3>{t('SELECT_A_STUDENT')}:</h3>
      <Select
        ref={selectAntd}
        showSearch
        showAction={['focus', 'click']}
        open={selectOpen}
        onClick={() => changeSelectOpen(!selectOpen)}
        onBlur={() => changeSelectOpen(false)}
        loading={loading}
        placeholder={t('START_TYPING')}
        notFoundContent={loading ? null : t('NO_DATA')}
        filterOption={() => true}
        onSearch={handleSearch}
        onChange={handleChange}
        dropdownClassName='custom_dropdown'
        className='custom_select'
        defaultActiveFirstOption={false}
        defaultValue={singleStudent?.studentId}
        autoFocus
        data-cy='studentSearch'
      >
        {singleStudent && !studentList.includes(singleStudent) ? (
          <Option
            hidden
            key={`${singleStudent.studentId}_${singleStudent.lp}`}
            className='custom_option'
            value={singleStudent.studentId}
          >
            {singleStudent.studentImie} {singleStudent.studentNazwisko} {singleStudent.studentAlbum}
          </Option>
        ) : null}
        {options}
      </Select>
    </StudentSearchContainer>
  );
};

// użycie hoca zamiast hooka spowodowane chęcią zachowania jednolitości (i tak by trzeba było opakować inne elementy logiki)
const mapStateToProps = (state: RootState): studentFinderActionsInterfaces.StudentFinderState => ({
  selectOpen: studentFinderActionsSelectors.getSelectOpenStatus(state),
  loading: studentFinderActionsSelectors.getLoadingStatus(state),
  studentList: studentFinderActionsSelectors.getStudentListData(state),
  singleStudent: studentFinderActionsSelectors.getSingleStudent(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  getStudentList: (search: string): Array<SingleObjectDataStructure> => dispatch(getStudentList(search)),
  getStudentListCancel: (): void => dispatch(getStudentListCancel()),
  chooseSingleStudent: (studentId: number): SingleObjectDataStructure | undefined => dispatch(chooseSingleStudent(studentId)),
  changeSelectOpen: (opened: boolean): boolean => dispatch(changeSelectOpen(opened)),
});

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(StudentFinder) as React.ComponentType<any>;
